import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { DatovkaApiType, OsobyFyzickaOsobaDto } from '@gov-nx/api';
import {
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useIdentity } from '@gov-nx/auth/common';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import type {
	FormWizardHook,
	PersonCommunicationData,
} from '@gov-nx/core/hooks';
import {
	useCountryCodes,
	usePersonCommunicationData,
	usePersonCommunicationForm,
	useRobPersonQuery,
	useWizard,
} from '@gov-nx/core/hooks';
import { getRequiredDataBoxes } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { useDataBoxAccess } from '@gov-nx/module/data-box';
import { dataBoxParams, phoneShapeToString } from '@gov-nx/utils/common';
import { ServiceCode } from '../../definitions/codes';
import { useKorespondencniVolbyLocale } from '../useKorespondencniVolbyLocale';
import type {
	KorespondencniHlasovaniVymazFormStep1,
	KorespondencniHlasovaniVymazStep1,
} from './useFormStep1';
import { useFormStep1 } from './useFormStep1';
import type {
	KorespondencniHlasovaniVymazFormStep2,
	KorespondencniHlasovaniVymazStep2,
} from './useFormStep2';
import { useFormStep2 } from './useFormStep2';
import type {
	KorespondencniHlasovaniVymazFormStep3,
	KorespondencniHlasovaniVymazStep3,
} from './useFormStep3';
import { useFormStep3 } from './useFormStep3';

interface KorespondencniHlasovaniVymazContext {
	wizard: FormWizardHook<VymazForm>;
	step1: KorespondencniHlasovaniVymazFormStep1;
	step2: KorespondencniHlasovaniVymazFormStep2;
	step3: KorespondencniHlasovaniVymazFormStep3;
	personCommunicationData: PersonCommunicationData;
	isLoading: boolean;
	isSubmitting: boolean;
	person?: OsobyFyzickaOsobaDto;
	hideAddressInputs: boolean;
	showConnectDataboxBanner: boolean;
	databoxTypes: DatovkaApiType['typSchranky'][];
	isCzechCitizen: boolean;
	recapKeyValues: { label: string; value: string }[];
}

type VymazForm = KorespondencniHlasovaniVymazStep1 &
	KorespondencniHlasovaniVymazStep2 &
	KorespondencniHlasovaniVymazStep3;

const ServiceContext =
	createContext<Nullable<KorespondencniHlasovaniVymazContext>>(null);

interface Props {
	children: ReactNode;
	onSubmitSuccess: () => void;
}

export const KorespondencniHlasovaniVymazContextProvider = ({
	children,
	onSubmitSuccess,
}: Props) => {
	const {
		extraConfig: { application },
	} = useExtraConfigContext();
	const databoxTypes = getRequiredDataBoxes(
		ServiceCode['zadost-o-korespondencni-hlasovani'],
		application
	);

	const personQuery = useRobPersonQuery();
	const countryCodes = useCountryCodes();
	const { t } = useKorespondencniVolbyLocale();
	const { toastMessageSuccess } = useMessageEvents();
	const { isDataBoxLoginType } = useIdentity();
	const { areAnyConnected } = useDataBoxAccess({
		requiredTypes: databoxTypes,
	});
	const { isCzechCitizen } = useRobPersonQuery();
	const { axiosInstance } = useAxiosInstance();

	const submitMutation = usePoMutation({
		mutationFn: async (variables: VymazForm) => {
			const { datovaSchrankaId, ...params } = dataBoxParams(variables);

			return sendSubmissionQuery(axiosInstance)({
				params: {
					odesilatelDSId: datovaSchrankaId,
					...params,
				},
				body: {
					kodPodani: 'ZADOST_VYMAZ_ZVLASTNI_SEZNAM_VOLICU',
					zuKod: variables.officeId,
					adresaUlice: variables.street,
					adresaMesto: variables.city,
					adresaPsc: variables.zip,
					adresaStat: variables.country,
					zpusobPrevzeti: variables.feedBackMethod,
					email: variables._useEmail ? variables.email : undefined,
					telefon:
						variables._usePhone && variables.phone
							? phoneShapeToString(variables.phone)
							: undefined,
				},
			});
		},
		onSuccess: () => {
			toastMessageSuccess(t('vymaz.zpravy.odeslano'));
			wizard.resetForms();
			onSubmitSuccess();
		},
	});

	const isSubmitting = submitMutation.isLoading;
	const personCommunicationData = usePersonCommunicationData();

	const step1 = useFormStep1({ isDisabled: isSubmitting });
	const step2 = useFormStep2({ isDisabled: isSubmitting });
	const step3 = useFormStep3({
		isDisabled: isSubmitting,
		databoxTypes,
	});

	usePersonCommunicationForm(step1, {
		countryCodes,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});

	const wizard = useWizard<VymazForm>({
		steps: [step1, step2.form, step3],
		onSubmit: async (values) => submitMutation.mutate(values),
		onStepInvalid: (index, errors) => {
			if (index === 0) {
				personCommunicationData.onFormValidated(errors);
			}
		},
	});

	const { feedBackMethod, city, zip, street, country } =
		step2.form.formMethods.getValues();

	const recapKeyValues = [
		{
			label: t('vymaz.rekapitulace.udaje-pro-vymaz.zastupitelsky-urad'),
			value: `${step2.selectedCountry}, ${step2.selectedDistrict}, ${step2.selectedOffice}`,
		},
		{
			label: t('vymaz.rekapitulace.udaje-pro-vymaz.zpusob-prevzeti.klic'),
			value: feedBackMethod
				? {
						POSTA: `${t(
							'vymaz.rekapitulace.udaje-pro-vymaz.zpusob-prevzeti.posta'
						)}: ${street}, ${zip}, ${city} , ${country}`,
						OSOBNE: t(
							'vymaz.rekapitulace.udaje-pro-vymaz.zpusob-prevzeti.osobne'
						),
						DATOVA_SCHRANKA: t(
							'vymaz.rekapitulace.udaje-pro-vymaz.zpusob-prevzeti.datovka'
						),
					}[feedBackMethod]
				: '',
		},
	];

	return (
		<ServiceContext.Provider
			value={{
				isLoading:
					personQuery.isLoading || step2.isLoading || countryCodes.isLoading,
				wizard,
				personCommunicationData,
				step1,
				step2: step2.form,
				step3,
				isSubmitting,
				person: personQuery.data,
				hideAddressInputs: step2.hideAddressInputs,
				showConnectDataboxBanner: isDataBoxLoginType && !areAnyConnected,
				databoxTypes,
				isCzechCitizen,
				recapKeyValues,
			}}>
			{children}
		</ServiceContext.Provider>
	);
};

export const useKorespondencniHlasovaniVymazContext = () => {
	const context = useContext(ServiceContext);
	if (!context) {
		throw new Error('Context must be used within a ContextProvider');
	}
	return context;
};
